import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import api from '../utils/axios';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { StatCard } from './kpis/components/StatCard';
import { fmtMoney, fmtNum, fmtMoneyShort } from './kpis/components/kpiUtils';
import {
  Receipt, Download, Building2, Search, ArrowDownLeft, ArrowUpRight, Percent, Save,
  ChevronRight, ChevronDown, Pencil, Check, X, FileSpreadsheet, FileText, RefreshCw, Layers, ShieldOff, History, EyeOff, RotateCcw,
} from 'lucide-react';

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const RANGOS = [
  { id: 'mes_actual', label: 'Mes actual' },
  { id: 'mes_anterior', label: 'Mes anterior' },
  { id: 'ultimos_3', label: 'Últimos 3 meses' },
  { id: 'trimestre', label: 'Trimestre actual' },
  { id: 'anio', label: 'Año actual' },
  { id: 'personalizado', label: 'Personalizado' },
];

const calcRango = (id) => {
  const h = new Date();
  switch (id) {
    case 'mes_anterior': return [iso(new Date(h.getFullYear(), h.getMonth() - 1, 1)), iso(new Date(h.getFullYear(), h.getMonth(), 0))];
    case 'ultimos_3': return [iso(new Date(h.getFullYear(), h.getMonth() - 2, 1)), iso(h)];
    case 'trimestre': return [iso(new Date(h.getFullYear(), Math.floor(h.getMonth() / 3) * 3, 1)), iso(h)];
    case 'anio': return [iso(new Date(h.getFullYear(), 0, 1)), iso(h)];
    case 'mes_actual':
    default: return [iso(new Date(h.getFullYear(), h.getMonth(), 1)), iso(h)];
  }
};

const evLabel = (e) => {
  if (e.accion === 'exento') return e.valor ? 'Marcada exenta' : 'Quitada exención';
  if (e.accion === 'oculto') return e.valor ? 'Quitada de la lista' : 'Restaurada a la lista';
  return e.valor ? 'Incluida en el impuesto' : 'Excluida del impuesto';
};
const evBadgeClass = (e) => {
  if (e.accion === 'exento') return e.valor ? 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400' : 'bg-slate-100 text-slate-600';
  if (e.accion === 'oculto') return e.valor ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200' : 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400';
  return e.valor ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400' : 'bg-slate-100 text-slate-600';
};
const evColor = (e) => {
  if (e.accion === 'exento') return e.valor ? '#f43f5e' : '#94a3b8';
  if (e.accion === 'oculto') return e.valor ? '#64748b' : '#10b981';
  return e.valor ? '#10b981' : '#94a3b8';
};

const ImpuestoCheque = () => {
  const [tab, setTab] = useState('calculo');
  const [esAdmin, setEsAdmin] = useState(false);

  // ---- Cálculo ----
  const [rango, setRango] = useState('mes_actual');
  const [desde, setDesde] = useState(calcRango('mes_actual')[0]);
  const [hasta, setHasta] = useState(calcRango('mes_actual')[1]);
  const [reporte, setReporte] = useState(null);
  const [loadingRep, setLoadingRep] = useState(true);
  const [tasa, setTasa] = useState(0.006);
  const [tasaEdit, setTasaEdit] = useState('0.6');
  const [expanded, setExpanded] = useState(new Set());

  // detalle
  const [detOpen, setDetOpen] = useState(false);
  const [detData, setDetData] = useState(null);
  const [detCuenta, setDetCuenta] = useState(null);
  const [detLoading, setDetLoading] = useState(false);

  // informe
  const [infOpen, setInfOpen] = useState(false);
  const [infFormato, setInfFormato] = useState('excel');
  const [infDetalle, setInfDetalle] = useState(true);
  const [infModo, setInfModo] = useState('todas');
  const [infSel, setInfSel] = useState(new Set());
  const [generando, setGenerando] = useState(false);

  // ---- Jurídicas ----
  const [jur, setJur] = useState({ cuentas: [], total: 0, total_juridicas: 0 });
  const [jurLoading, setJurLoading] = useState(false);
  const [jurSearch, setJurSearch] = useState('');
  const [jurFiltro, setJurFiltro] = useState('todas');
  const [aliasCuit, setAliasCuit] = useState(null);
  const [aliasVal, setAliasVal] = useState('');
  const [nuevasCuits, setNuevasCuits] = useState(new Set());
  const [barriendo, setBarriendo] = useState(false);
  const [exentoDlg, setExentoDlg] = useState({ open: false, cuenta: null });
  const [exentoMotivo, setExentoMotivo] = useState('');
  const [ocultarDlg, setOcultarDlg] = useState({ open: false, cuenta: null });
  const [ocultarMotivo, setOcultarMotivo] = useState('');
  const [histDlg, setHistDlg] = useState({ open: false, cuenta: null, loading: false, eventos: [] });
  const reqIdRef = useRef(0);

  const cargarReporte = useCallback(async (d = desde, h = hasta) => {
    const myId = ++reqIdRef.current;
    setLoadingRep(true);
    try {
      const r = await api.get('/impuesto-cheque/reporte', { params: { fecha_desde: d, fecha_hasta: h } });
      if (myId !== reqIdRef.current) return; // respuesta obsoleta: ignorar
      setReporte(r.data);
      setTasa(r.data.tasa);
      setTasaEdit(String((r.data.tasa * 100).toFixed(3).replace(/\.?0+$/, '')));
      setEsAdmin(r.data.es_admin);
      setExpanded(new Set());
    } catch (e) { if (myId === reqIdRef.current) toast.error('Error al calcular el reporte'); }
    finally { if (myId === reqIdRef.current) setLoadingRep(false); }
  }, [desde, hasta]);

  useEffect(() => { cargarReporte(); /* solo al montar */ }, []); // eslint-disable-line

  const onRango = (id) => {
    setRango(id);
    if (id !== 'personalizado') {
      const [d, h] = calcRango(id);
      setDesde(d); setHasta(h);
      cargarReporte(d, h);
    }
  };

  const cargarJuridicas = useCallback(async (incluirOcultas = false) => {
    setJurLoading(true);
    try {
      const r = await api.get('/impuesto-cheque/juridicas', { params: incluirOcultas ? { incluir_ocultas: true } : {} });
      setJur(r.data); setEsAdmin(r.data.es_admin);
    } catch (e) { toast.error('Error al cargar personas jurídicas'); }
    finally { setJurLoading(false); }
  }, []);

  useEffect(() => { if (tab === 'juridicas' && jur.cuentas.length === 0) cargarJuridicas(); }, [tab, cargarJuridicas, jur.cuentas.length]);

  const guardarTasa = async () => {
    const pct = parseFloat(tasaEdit);
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error('Tasa inválida (0 a 100 %)'); return; }
    try {
      await api.put('/impuesto-cheque/config', { tasa: pct / 100 });
      toast.success(`Alícuota guardada: ${pct}%`);
      cargarReporte();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar la tasa'); }
  };

  const abrirDetalle = async (cuenta) => {
    setDetCuenta(cuenta); setDetOpen(true); setDetLoading(true); setDetData(null);
    try {
      const r = await api.get('/impuesto-cheque/reporte/detalle', { params: { cuit: cuenta.cuit, fecha_desde: desde, fecha_hasta: hasta } });
      setDetData(r.data);
    } catch (e) { toast.error('Error al cargar el detalle'); }
    finally { setDetLoading(false); }
  };

  const toggleExpand = (cuit, e) => {
    e.stopPropagation();
    setExpanded((prev) => { const n = new Set(prev); n.has(cuit) ? n.delete(cuit) : n.add(cuit); return n; });
  };

  const toggleJuridica = async (c, value) => {
    setJur((prev) => ({
      ...prev,
      cuentas: prev.cuentas.map((x) => (x.cuit === c.cuit ? { ...x, es_juridica: value, origen: value === x.es_auto && !x.alias ? 'auto' : 'manual' } : x)),
      total_juridicas: prev.total_juridicas + (value ? 1 : -1),
    }));
    try { await api.post('/impuesto-cheque/juridicas', { cuit: c.cuit, razon_social: c.razon_social, es_juridica: value }); }
    catch (e) { toast.error('No se pudo guardar el cambio'); cargarJuridicas(); }
  };

  const applyExento = async (c, value, motivo = '') => {
    setJur((prev) => ({
      ...prev,
      cuentas: prev.cuentas.map((x) => (x.cuit === c.cuit ? { ...x, exento: value } : x)),
      total_exentas: (prev.total_exentas || 0) + (value ? 1 : -1),
    }));
    try { await api.post('/impuesto-cheque/juridicas/exento', { cuit: c.cuit, exento: value, motivo }); }
    catch (e) { toast.error('No se pudo guardar la exención'); cargarJuridicas(); }
  };

  const onExentoChange = (c, value) => {
    if (value) { setExentoDlg({ open: true, cuenta: c }); setExentoMotivo(''); }
    else { applyExento(c, false, ''); }
  };

  const confirmExento = async () => {
    if (exentoDlg.cuenta) await applyExento(exentoDlg.cuenta, true, exentoMotivo);
    setExentoDlg({ open: false, cuenta: null }); setExentoMotivo('');
  };

  const applyOcultar = async (c, value, motivo = '') => {
    try {
      await api.post('/impuesto-cheque/juridicas/ocultar', { cuit: c.cuit, oculto: value, motivo });
      toast.success(value ? 'Cuenta quitada de la lista' : 'Cuenta restaurada a la lista');
      await cargarJuridicas(jurFiltro === 'ocultas');
      if (reporte) cargarReporte();
    } catch (e) { toast.error('No se pudo actualizar la lista'); }
  };
  const onOcultarChange = (c) => { setOcultarDlg({ open: true, cuenta: c }); setOcultarMotivo(''); };
  const confirmOcultar = async () => {
    if (ocultarDlg.cuenta) await applyOcultar(ocultarDlg.cuenta, true, ocultarMotivo);
    setOcultarDlg({ open: false, cuenta: null }); setOcultarMotivo('');
  };
  const onJurFiltro = (v) => { setJurFiltro(v); cargarJuridicas(v === 'ocultas'); };

  const abrirHistorial = async (c) => {
    setHistDlg({ open: true, cuenta: c, loading: true, eventos: [] });
    try {
      const r = await api.get(`/impuesto-cheque/juridicas/${c.cuit}/historial`);
      setHistDlg({ open: true, cuenta: c, loading: false, eventos: r.data.eventos || [] });
    } catch (e) { setHistDlg({ open: true, cuenta: c, loading: false, eventos: [] }); toast.error('No se pudo cargar el historial'); }
  };

  const barrido = async () => {
    setBarriendo(true);
    try {
      const r = await api.post('/impuesto-cheque/juridicas/barrido');
      const { nuevas = [], total_nuevas = 0, primera_vez, total_juridicas } = r.data;
      await cargarJuridicas();
      if (primera_vez) {
        setNuevasCuits(new Set());
        toast.success(`Línea base establecida: ${total_juridicas} cuentas jurídicas. A partir de ahora te aviso si aparecen nuevas.`);
      } else if (total_nuevas > 0) {
        setNuevasCuits(new Set(nuevas.map((n) => n.cuit)));
        setJurFiltro('nuevas');
        toast.success(`${total_nuevas} cuenta(s) nueva(s) detectada(s). Revisalas y excluí/exentá si corresponde.`);
      } else {
        setNuevasCuits(new Set());
        toast.info('No hay cuentas nuevas desde el último barrido.');
      }
    } catch (e) { toast.error('No se pudo hacer el barrido'); }
    finally { setBarriendo(false); }
  };

  const abrirAlias = (c) => { setAliasCuit(c.cuit); setAliasVal(c.alias || ''); };
  const guardarAlias = async (cuit) => {
    try {
      await api.post('/impuesto-cheque/juridicas/alias', { cuit, alias: aliasVal });
      toast.success('Alias guardado');
      setJur((prev) => ({ ...prev, cuentas: prev.cuentas.map((x) => (x.cuit === cuit ? { ...x, alias: aliasVal.trim() || null } : x)) }));
      setAliasCuit(null);
      if (reporte) cargarReporte();
    } catch (e) { toast.error('No se pudo guardar el alias'); }
  };

  const exportCSV = () => {
    if (!reporte?.cuentas?.length) return;
    const head = ['CUIT', 'Cuenta', 'CVUs', 'Entradas gravadas', 'Salidas gravadas', 'Base imponible', 'Impuesto', 'Transf. gravadas', 'Excluido cuenta propia'];
    const lines = reporte.cuentas.map((c) => [c.cuit, `"${(c.razon_social || '').replace(/"/g, '""')}"`, `"${(c.cvus || []).join(' ')}"`, c.entradas, c.salidas, c.base_imponible, c.impuesto, c.n_gravadas, c.excluido_propia].join(','));
    const csv = [head.join(','), ...lines].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `impuesto_cheque_${desde}_${hasta}.csv`; a.click();
  };

  const abrirInforme = () => {
    setInfSel(new Set((reporte?.cuentas || []).map((c) => c.cuit)));
    setInfModo('todas'); setInfFormato('excel'); setInfDetalle(true); setInfOpen(true);
  };
  const toggleInfSel = (cuit) => setInfSel((prev) => { const n = new Set(prev); n.has(cuit) ? n.delete(cuit) : n.add(cuit); return n; });

  const generarInforme = async () => {
    setGenerando(true);
    try {
      const cuits = infModo === 'sel' ? Array.from(infSel) : null;
      if (infModo === 'sel' && cuits.length === 0) { toast.error('Elegí al menos una cuenta'); setGenerando(false); return; }
      const resp = await api.post('/impuesto-cheque/informe',
        { fecha_desde: desde, fecha_hasta: hasta, formato: infFormato, incluir_detalle: infDetalle, cuits },
        { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `impuesto_cheque_${desde}_${hasta}.${infFormato === 'pdf' ? 'pdf' : 'xlsx'}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success('Informe generado');
      setInfOpen(false);
    } catch (e) { toast.error('No se pudo generar el informe'); }
    finally { setGenerando(false); }
  };

  const t = reporte?.totales || {};
  const cuentas = reporte?.cuentas || [];
  const jurFiltradas = useMemo(() => {
    const q = jurSearch.trim().toLowerCase();
    return jur.cuentas.filter((c) => {
      if (jurFiltro === 'ocultas') { if (!c.oculto) return false; }
      else if (c.oculto) return false;
      if (jurFiltro === 'juridicas' && !c.es_juridica) return false;
      if (jurFiltro === 'fisicas' && c.es_juridica) return false;
      if (jurFiltro === 'exentas' && !c.exento) return false;
      if (jurFiltro === 'nuevas' && !nuevasCuits.has(c.cuit)) return false;
      if (!q) return true;
      return c.cuit.includes(q) || (c.razon_social || '').toLowerCase().includes(q) || (c.alias || '').toLowerCase().includes(q);
    });
  }, [jur.cuentas, jurSearch, jurFiltro, nuevasCuits]);

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="impuesto-cheque">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-indigo-500/10"><Receipt className="h-6 w-6 text-indigo-600" /></div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Impuesto al cheque</h1>
          <p className="text-sm text-slate-500">Ley 25.413 · 0,6% sobre transferencias de personas jurídicas (excluye cuentas propias)</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6" data-testid="ic-tabs">
          <TabsTrigger value="calculo" data-testid="tab-calculo">Cálculo del impuesto</TabsTrigger>
          <TabsTrigger value="juridicas" data-testid="tab-juridicas">Personas jurídicas</TabsTrigger>
        </TabsList>

        {/* ================= CÁLCULO ================= */}
        <TabsContent value="calculo">
          <Card className="p-4 lg:p-5 mb-6" data-testid="ic-filtros">
            <div className="flex flex-col lg:flex-row lg:items-end gap-4 flex-wrap">
              <div className="w-full sm:w-52">
                <label className="text-xs font-medium text-slate-500 mb-1 block">Período</label>
                <Select value={rango} onValueChange={onRango}>
                  <SelectTrigger data-testid="ic-rango-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{RANGOS.map((r) => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {rango === 'personalizado' && (
                <>
                  <div><label className="text-xs font-medium text-slate-500 mb-1 block">Desde</label>
                    <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className="w-40" data-testid="ic-fecha-desde" /></div>
                  <div><label className="text-xs font-medium text-slate-500 mb-1 block">Hasta</label>
                    <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="w-40" data-testid="ic-fecha-hasta" /></div>
                </>
              )}
              <Button onClick={() => cargarReporte()} disabled={loadingRep} className="bg-indigo-600 hover:bg-indigo-700 active:scale-95 transition-transform" data-testid="ic-calcular-btn">
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingRep ? 'animate-spin' : ''}`} /> Calcular impuesto
              </Button>
              <div className="lg:ml-auto flex items-end gap-2">
                <div>
                  <label className="text-xs font-medium text-slate-500 mb-1 flex items-center gap-1"><Percent className="h-3 w-3" /> Alícuota (%)</label>
                  <Input type="number" step="0.001" value={tasaEdit} onChange={(e) => setTasaEdit(e.target.value)} disabled={!esAdmin} className="w-24" data-testid="ic-tasa-input" />
                </div>
                {esAdmin && <Button variant="outline" onClick={guardarTasa} data-testid="ic-tasa-guardar"><Save className="h-4 w-4 mr-1" /> Guardar</Button>}
                <Button variant="outline" onClick={exportCSV} data-testid="ic-export-btn"><Download className="h-4 w-4 mr-1" /> CSV</Button>
                <Button onClick={abrirInforme} className="bg-emerald-600 hover:bg-emerald-700 active:scale-95 transition-transform" data-testid="ic-informe-btn"><FileText className="h-4 w-4 mr-1" /> Generar informe</Button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-3">
              Período: <b>{desde}</b> a <b>{hasta}</b> · Alícuota aplicada: <b>{(tasa * 100).toFixed(3).replace(/\.?0+$/, '')}%</b>
              {t.cuentas_exentas > 0 && <span className="text-rose-500"> · {t.cuentas_exentas} cuenta(s) exenta(s) excluida(s)</span>}
            </p>
          </Card>

          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <StatCard label="Impuesto total" value={loadingRep ? '…' : fmtMoneyShort(t.impuesto_total)} icon={Receipt} accent="#4f46e5" deltaLabel={loadingRep ? '' : fmtMoney(t.impuesto_total)} testId="ic-kpi-impuesto" />
            <StatCard label="Base imponible" value={loadingRep ? '…' : fmtMoneyShort(t.base_imponible)} icon={ArrowUpRight} accent="#0ea5e9" deltaLabel={loadingRep ? '' : fmtMoney(t.base_imponible)} testId="ic-kpi-base" />
            <StatCard label="Transf. gravadas" value={loadingRep ? '…' : fmtNum(t.transferencias_gravadas)} icon={ArrowDownLeft} accent="#10b981" testId="ic-kpi-gravadas" />
            <StatCard label="Cuentas jurídicas" value={loadingRep ? '…' : fmtNum(t.cuentas_juridicas)} icon={Building2} accent="#6366f1" testId="ic-kpi-cuentas" />
            <StatCard label="Excluido (cta. propia)" value={loadingRep ? '…' : fmtMoneyShort(t.excluido_cuenta_propia)} icon={ArrowUpRight} accent="#3b82f6" deltaLabel={loadingRep ? '' : fmtMoney(t.excluido_cuenta_propia)} testId="ic-kpi-excluido" />
          </div>

          <Card className="p-0 overflow-hidden" data-testid="ic-tabla-cuentas">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800">
              <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-200">Impuesto por cuenta jurídica</h2>
              <p className="text-xs text-slate-400 mt-0.5">Clic en la fila para ver el detalle · en la flecha para ver los CVUs (sucursales).</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                    <th className="w-8 px-2 py-2.5" />
                    <th className="text-left font-medium px-4 py-2.5">CUIT</th>
                    <th className="text-left font-medium px-4 py-2.5">Cuenta</th>
                    <th className="text-center font-medium px-2 py-2.5">CVUs</th>
                    <th className="text-right font-medium px-4 py-2.5">Entradas</th>
                    <th className="text-right font-medium px-4 py-2.5">Salidas</th>
                    <th className="text-right font-medium px-4 py-2.5">Base imponible</th>
                    <th className="text-right font-medium px-4 py-2.5">Impuesto</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRep && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Calculando…</td></tr>}
                  {!loadingRep && cuentas.map((c) => (
                    <React.Fragment key={c.cuit}>
                      <tr onClick={() => abrirDetalle(c)} className="border-t border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer" data-testid={`ic-cuenta-row-${c.cuit}`}>
                        <td className="px-2 py-2.5 text-center">
                          {(c.cvus || []).length > 0 && (
                            <button onClick={(e) => toggleExpand(c.cuit, e)} className="text-slate-400 hover:text-indigo-600" data-testid={`ic-expand-${c.cuit}`}>
                              {expanded.has(c.cuit) ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          )}
                        </td>
                        <td className="px-4 py-2.5 font-mono text-xs">{c.cuit}</td>
                        <td className="px-4 py-2.5 font-medium text-slate-700 dark:text-slate-200">{c.razon_social}</td>
                        <td className="px-2 py-2.5 text-center"><Badge variant="secondary" className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{(c.cvus || []).length}</Badge></td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-emerald-600">{fmtMoney(c.entradas)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums text-rose-600">{fmtMoney(c.salidas)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums">{fmtMoney(c.base_imponible)}</td>
                        <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-indigo-600">{fmtMoney(c.impuesto)}</td>
                      </tr>
                      {expanded.has(c.cuit) && (
                        <tr className="bg-slate-50/60 dark:bg-slate-800/30" data-testid={`ic-cvus-${c.cuit}`}>
                          <td />
                          <td colSpan={7} className="px-4 py-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs text-slate-400 flex items-center gap-1"><Layers className="h-3.5 w-3.5" /> Sucursales / CVUs:</span>
                              {(c.cvus || []).map((v) => (
                                <span key={v} className="font-mono text-xs bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded px-2 py-0.5">{v}</span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {!loadingRep && cuentas.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No hay transferencias gravadas en el período. Probá otro período y presioná «Calcular impuesto».</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>

        {/* ================= JURÍDICAS ================= */}
        <TabsContent value="juridicas">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <StatCard label="Cuentas totales" value={jur.total} icon={Building2} accent="#6366f1" testId="ic-jur-kpi-total" />
            <StatCard label="Marcadas jurídicas" value={jur.total_juridicas} icon={Building2} accent="#4f46e5" testId="ic-jur-kpi-juridicas" />
            <StatCard label="Exentas (sin impuesto)" value={jur.total_exentas || 0} icon={ShieldOff} accent="#f43f5e" testId="ic-jur-kpi-exentas" />
            <StatCard label="Quitadas (ocultas)" value={jur.total_ocultas || 0} icon={EyeOff} accent="#64748b" testId="ic-jur-kpi-ocultas" />
          </div>

          <Card className="p-0 overflow-hidden" data-testid="ic-juridicas-card">
            <div className="p-5 border-b border-slate-100 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center gap-3">
              <div>
                <h2 className="text-lg font-semibold tracking-tight text-slate-800 dark:text-slate-200">Personas jurídicas</h2>
                <p className="text-xs text-slate-400 mt-0.5">Autodetectadas por CUIT (30/33/34). {esAdmin ? 'Marcá/desmarcá jurídicas, exentá cuentas y asigná un alias.' : 'Solo lectura.'}</p>
              </div>
              <div className="sm:ml-auto flex items-center gap-2 flex-wrap">
                {esAdmin && (
                  <Button variant="outline" onClick={barrido} disabled={barriendo} data-testid="ic-barrido-btn">
                    <RefreshCw className={`h-4 w-4 mr-1 ${barriendo ? 'animate-spin' : ''}`} /> Buscar cuentas nuevas
                  </Button>
                )}
                <div className="relative">
                  <Search className="h-4 w-4 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                  <Input value={jurSearch} onChange={(e) => setJurSearch(e.target.value)} placeholder="Buscar CUIT, nombre o alias" className="pl-8 w-64" data-testid="ic-juridicas-search" />
                </div>
                <Select value={jurFiltro} onValueChange={onJurFiltro}>
                  <SelectTrigger className="w-36" data-testid="ic-juridicas-filter"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todas">Todas</SelectItem>
                    <SelectItem value="juridicas">Solo jurídicas</SelectItem>
                    <SelectItem value="fisicas">Solo físicas</SelectItem>
                    <SelectItem value="exentas">Solo exentas</SelectItem>
                    <SelectItem value="ocultas">Ocultas ({jur.total_ocultas || 0})</SelectItem>
                    {nuevasCuits.size > 0 && <SelectItem value="nuevas">Solo nuevas ({nuevasCuits.size})</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="overflow-x-auto max-h-[560px]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <tr className="bg-slate-50 dark:bg-slate-800 text-slate-500">
                    <th className="text-left font-medium px-4 py-2.5">CUIT</th>
                    <th className="text-left font-medium px-4 py-2.5">Nombre (del movimiento)</th>
                    <th className="text-left font-medium px-4 py-2.5">Alias (a mostrar)</th>
                    <th className="text-left font-medium px-4 py-2.5">Origen</th>
                    <th className="text-center font-medium px-4 py-2.5">¿Es jurídica?</th>
                    <th className="text-center font-medium px-4 py-2.5">Exento</th>
                    <th className="text-center font-medium px-4 py-2.5">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {jurLoading && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Cargando… (el primer barrido del universo puede tardar unos segundos)</td></tr>}
                  {!jurLoading && jurFiltradas.map((c) => (
                    <tr key={c.cuit} className={`border-t border-slate-100 dark:border-slate-800 ${nuevasCuits.has(c.cuit) ? 'bg-indigo-50/60 dark:bg-indigo-500/5' : ''}`} data-testid={`ic-jur-row-${c.cuit}`}>
                      <td className="px-4 py-2.5 font-mono text-xs">
                        <div className="flex items-center gap-2">
                          <span>{c.cuit}</span>
                          {nuevasCuits.has(c.cuit) && <Badge className="bg-indigo-600 text-white hover:bg-indigo-600" data-testid={`ic-jur-nueva-${c.cuit}`}>Nueva</Badge>}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300">{c.razon_social}</td>
                      <td className="px-4 py-2.5">
                        {aliasCuit === c.cuit ? (
                          <div className="flex items-center gap-1">
                            <Input value={aliasVal} onChange={(e) => setAliasVal(e.target.value)} placeholder="Alias…" className="h-8 w-48" autoFocus data-testid={`ic-alias-input-${c.cuit}`} />
                            <button onClick={() => guardarAlias(c.cuit)} className="text-emerald-600 p-1" title="Guardar" data-testid={`ic-alias-save-${c.cuit}`}><Check className="h-4 w-4" /></button>
                            <button onClick={() => setAliasCuit(null)} className="text-slate-400 p-1" title="Cancelar"><X className="h-4 w-4" /></button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {c.alias
                              ? <span className="font-medium text-indigo-600">{c.alias}</span>
                              : <span className="text-slate-300 text-xs">Sin alias</span>}
                            {esAdmin && <button onClick={() => abrirAlias(c)} className="text-slate-400 hover:text-indigo-600" title="Editar alias" data-testid={`ic-alias-edit-${c.cuit}`}><Pencil className="h-3.5 w-3.5" /></button>}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5">
                        {c.origen === 'auto' && <Badge variant="secondary" className="bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-400">Auto</Badge>}
                        {c.origen === 'manual' && <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400">Manual</Badge>}
                        {c.origen === 'no' && <span className="text-slate-300 text-xs">—</span>}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <Switch checked={c.es_juridica} disabled={!esAdmin} onCheckedChange={(v) => toggleJuridica(c, v)} data-testid={`ic-jur-switch-${c.cuit}`} />
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-2">
                          <Switch checked={!!c.exento} disabled={!esAdmin || !c.es_juridica} onCheckedChange={(v) => onExentoChange(c, v)} data-testid={`ic-exento-switch-${c.cuit}`} />
                          {c.exento && <Badge variant="secondary" className="bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-400">Exento</Badge>}
                          <button onClick={() => abrirHistorial(c)} className="text-slate-400 hover:text-indigo-600" title="Ver historial" data-testid={`ic-hist-btn-${c.cuit}`}><History className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {esAdmin && (c.oculto
                          ? <Button size="sm" variant="ghost" className="h-7 text-emerald-600 hover:text-emerald-700" onClick={() => applyOcultar(c, false)} data-testid={`ic-restaurar-btn-${c.cuit}`}><RotateCcw className="h-3.5 w-3.5 mr-1" /> Restaurar</Button>
                          : <Button size="sm" variant="ghost" className="h-7 text-slate-400 hover:text-rose-600" onClick={() => onOcultarChange(c)} title="Quitar de la lista" data-testid={`ic-ocultar-btn-${c.cuit}`}><EyeOff className="h-3.5 w-3.5 mr-1" /> Quitar</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {!jurLoading && jurFiltradas.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">Sin resultados.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detalle dialog */}
      <Dialog open={detOpen} onOpenChange={setDetOpen}>
        <DialogContent className="max-w-3xl" data-testid="ic-detalle-dialog">
          <DialogHeader>
            <DialogTitle>Detalle · {detCuenta?.razon_social}</DialogTitle>
            <DialogDescription>
              CUIT {detCuenta?.cuit} · {desde} a {hasta} · impuesto {fmtMoney(detCuenta?.impuesto)}
              {(detData?.cvus?.length || detCuenta?.cvus?.length) ? ` · CVUs: ${(detData?.cvus || detCuenta?.cvus || []).join(', ')}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="overflow-auto max-h-[60vh]">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 dark:bg-slate-800 text-slate-500 sticky top-0">
                  <th className="text-left font-medium px-3 py-2">Fecha</th>
                  <th className="text-left font-medium px-3 py-2">Sentido</th>
                  <th className="text-left font-medium px-3 py-2">Contraparte</th>
                  <th className="text-right font-medium px-3 py-2">Monto</th>
                  <th className="text-center font-medium px-3 py-2">Estado</th>
                  <th className="text-right font-medium px-3 py-2">Impuesto</th>
                </tr>
              </thead>
              <tbody>
                {detLoading && <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Cargando…</td></tr>}
                {!detLoading && (detData?.detalle || []).map((d, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                    <td className="px-3 py-2 whitespace-nowrap text-slate-500">{(d.fecha || '').slice(0, 16)}</td>
                    <td className="px-3 py-2"><span className={d.sentido === 'INGRESO' ? 'text-emerald-600' : 'text-rose-600'}>{d.sentido === 'INGRESO' ? '↓ Entrada' : '↑ Salida'}</span></td>
                    <td className="px-3 py-2">{d.contraparte}<span className="block text-[11px] text-slate-400 font-mono">{d.contraparte_cuit}</span></td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtMoney(d.monto)}</td>
                    <td className="px-3 py-2 text-center">
                      {d.gravada
                        ? <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400">Gravada</Badge>
                        : <Badge variant="secondary" className="bg-slate-100 text-slate-500">Cta. propia</Badge>}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums font-medium text-indigo-600">{d.gravada ? fmtMoney(d.impuesto) : '—'}</td>
                  </tr>
                ))}
                {!detLoading && (detData?.detalle || []).length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-8 text-center text-slate-400">Sin movimientos.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>

      {/* Informe dialog */}
      <Dialog open={infOpen} onOpenChange={setInfOpen}>
        <DialogContent data-testid="ic-informe-dialog">
          <DialogHeader>
            <DialogTitle>Generar informe</DialogTitle>
            <DialogDescription>Período {desde} a {hasta} · alícuota {(tasa * 100).toFixed(3).replace(/\.?0+$/, '')}%</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">Formato</label>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setInfFormato('excel')} className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${infFormato === 'excel' ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10' : 'border-slate-200 dark:border-slate-700'}`} data-testid="ic-inf-excel">
                  <FileSpreadsheet className="h-5 w-5 text-emerald-600" /> <span className="text-sm font-medium">Excel (.xlsx)</span>
                </button>
                <button onClick={() => setInfFormato('pdf')} className={`flex items-center gap-2 p-3 rounded-lg border-2 transition-colors ${infFormato === 'pdf' ? 'border-rose-500 bg-rose-50 dark:bg-rose-500/10' : 'border-slate-200 dark:border-slate-700'}`} data-testid="ic-inf-pdf">
                  <FileText className="h-5 w-5 text-rose-600" /> <span className="text-sm font-medium">PDF</span>
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium text-slate-600 dark:text-slate-300">Incluir detalle de movimientos</p>
                <p className="text-xs text-slate-400">Suma una hoja/sección con cada transferencia.</p></div>
              <Switch checked={infDetalle} onCheckedChange={setInfDetalle} data-testid="ic-inf-detalle" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">Cuentas</label>
              <div className="flex gap-2 mb-2">
                <Button size="sm" variant={infModo === 'todas' ? 'default' : 'outline'} onClick={() => setInfModo('todas')} data-testid="ic-inf-todas">Todas ({cuentas.length})</Button>
                <Button size="sm" variant={infModo === 'sel' ? 'default' : 'outline'} onClick={() => setInfModo('sel')} data-testid="ic-inf-seleccionar">Seleccionar</Button>
              </div>
              {infModo === 'sel' && (
                <div className="max-h-44 overflow-auto border border-slate-200 dark:border-slate-700 rounded-lg p-2 space-y-1" data-testid="ic-inf-lista">
                  {cuentas.map((c) => (
                    <label key={c.cuit} className="flex items-center gap-2 text-sm px-1 py-1 hover:bg-slate-50 dark:hover:bg-slate-800 rounded cursor-pointer">
                      <input type="checkbox" checked={infSel.has(c.cuit)} onChange={() => toggleInfSel(c.cuit)} data-testid={`ic-inf-chk-${c.cuit}`} />
                      <span className="font-mono text-xs text-slate-400">{c.cuit}</span>
                      <span className="text-slate-700 dark:text-slate-200 truncate">{c.razon_social}</span>
                    </label>
                  ))}
                  {cuentas.length === 0 && <p className="text-xs text-slate-400 px-1">No hay cuentas en el período.</p>}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInfOpen(false)}>Cancelar</Button>
            <Button onClick={generarInforme} disabled={generando} className="bg-emerald-600 hover:bg-emerald-700" data-testid="ic-inf-generar">
              {generando ? <><RefreshCw className="h-4 w-4 mr-1 animate-spin" /> Generando…</> : <><Download className="h-4 w-4 mr-1" /> Generar y descargar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Exento motivo dialog */}
      <Dialog open={exentoDlg.open} onOpenChange={(o) => { if (!o) { setExentoDlg({ open: false, cuenta: null }); setExentoMotivo(''); } }}>
        <DialogContent data-testid="ic-exento-dialog">
          <DialogHeader>
            <DialogTitle>Marcar como exenta</DialogTitle>
            <DialogDescription>
              {exentoDlg.cuenta?.alias || exentoDlg.cuenta?.razon_social} · CUIT {exentoDlg.cuenta?.cuit}. No tributará el impuesto al cheque.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1 block">Motivo (opcional)</label>
            <Input value={exentoMotivo} onChange={(e) => setExentoMotivo(e.target.value)} placeholder="Ej: Entidad exenta por Ley…" data-testid="ic-exento-motivo" autoFocus />
            <p className="text-xs text-slate-400 mt-1">Queda registrado en el historial (quién y cuándo).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setExentoDlg({ open: false, cuenta: null }); setExentoMotivo(''); }}>Cancelar</Button>
            <Button onClick={confirmExento} className="bg-rose-600 hover:bg-rose-700" data-testid="ic-exento-confirm"><ShieldOff className="h-4 w-4 mr-1" /> Marcar exenta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Ocultar (quitar de la lista) dialog */}
      <Dialog open={ocultarDlg.open} onOpenChange={(o) => { if (!o) { setOcultarDlg({ open: false, cuenta: null }); setOcultarMotivo(''); } }}>
        <DialogContent data-testid="ic-ocultar-dialog">
          <DialogHeader>
            <DialogTitle>Quitar de la lista</DialogTitle>
            <DialogDescription>
              {ocultarDlg.cuenta?.alias || ocultarDlg.cuenta?.razon_social} · CUIT {ocultarDlg.cuenta?.cuit}. Se sacará de esta pestaña y NO tributará el impuesto al cheque. Podés restaurarla desde el filtro «Ocultas».
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1 block">Motivo (opcional)</label>
            <Input value={ocultarMotivo} onChange={(e) => setOcultarMotivo(e.target.value)} placeholder="Ej: No es una cuenta jurídica real…" data-testid="ic-ocultar-motivo" autoFocus />
            <p className="text-xs text-slate-400 mt-1">Queda registrado en el historial (quién y cuándo).</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setOcultarDlg({ open: false, cuenta: null }); setOcultarMotivo(''); }}>Cancelar</Button>
            <Button onClick={confirmOcultar} className="bg-slate-700 hover:bg-slate-800" data-testid="ic-ocultar-confirm"><EyeOff className="h-4 w-4 mr-1" /> Quitar de la lista</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Historial dialog */}
      <Dialog open={histDlg.open} onOpenChange={(o) => { if (!o) setHistDlg({ open: false, cuenta: null, loading: false, eventos: [] }); }}>
        <DialogContent className="max-w-2xl" data-testid="ic-historial-dialog">
          <DialogHeader>
            <DialogTitle>Historial · {histDlg.cuenta?.alias || histDlg.cuenta?.razon_social}</DialogTitle>
            <DialogDescription>CUIT {histDlg.cuenta?.cuit} · auditoría de inclusión, exención y quitado</DialogDescription>
          </DialogHeader>
          <div className="max-h-[55vh] overflow-auto">
            {histDlg.loading && <p className="text-sm text-slate-400 py-6 text-center">Cargando…</p>}
            {!histDlg.loading && histDlg.eventos.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Sin movimientos registrados para esta cuenta.</p>}
            {!histDlg.loading && histDlg.eventos.length > 0 && (
              <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-2 space-y-4 py-2">
                {histDlg.eventos.map((e, i) => (
                  <li key={i} className="ml-4" data-testid={`ic-hist-evento-${i}`}>
                    <div className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border-2 border-white dark:border-slate-900" style={{ background: evColor(e) }} />
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className={evBadgeClass(e)}>{evLabel(e)}</Badge>
                      <Badge variant="secondary" className="bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">{e.origen === 'padron' ? 'Desde Padrón' : 'Personas jurídicas'}</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{(e.timestamp || '').replace('T', ' ').slice(0, 16)} · {e.user_email || '—'}</p>
                    {e.motivo && <p className="text-sm text-slate-600 dark:text-slate-300 mt-0.5">Motivo: {e.motivo}</p>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImpuestoCheque;
